/**
 * Pure tank geometry helpers, shared by the public tank calculator and the
 * admin tank/reading tools. No browser or server dependencies.
 */
export type TankShape = "horizontal" | "vertical";

/**
 * Approved horizontal-tank curve, calibrated against the office fill chart for
 * a 275 gallon tank at a 95% working capacity limit:
 *   30% -> 183 gal of room, 50% -> 124 gal, 75% -> 58 gal.
 * Neither a straight-line reading nor a textbook circular-segment conversion
 * hits all three, so the chart values are the authority: the anchors below are
 * those readings expressed as a fraction of nominal capacity, with linear
 * interpolation in between. Monotone by construction.
 */
const GAUGE_ANCHORS: ReadonlyArray<readonly [gauge: number, fill: number]> = [
  [0, 0],
  [30, 0.284545],
  [50, 0.499091],
  [75, 0.739091],
  [100, 1],
];

export function horizontalVolumeFraction(depthFraction: number): number {
  const g = Math.min(1, Math.max(0, depthFraction)) * 100;
  for (let i = 1; i < GAUGE_ANCHORS.length; i++) {
    const [g0, f0] = GAUGE_ANCHORS[i - 1]!;
    const [g1, f1] = GAUGE_ANCHORS[i]!;
    if (g <= g1) {
      return f0 + ((g - g0) / (g1 - g0)) * (f1 - f0);
    }
  }
  return 1;
}

/**
 * A tank is never filled to its nominal rating: the top few inches are left as
 * expansion / vent space. We deliver to 95% of nominal capacity.
 */
export const WORKING_CAPACITY_FRACTION = 0.95;

export function defaultWorkingGallons(nominalGallons: number): number {
  return Math.round(nominalGallons * WORKING_CAPACITY_FRACTION);
}

/** Volume fraction of the tank for a gauge/depth percentage (0-100). */
export function volumeFraction(gaugePercent: number, shape: TankShape): number {
  const g = Math.min(100, Math.max(0, Number(gaugePercent) || 0)) / 100;
  return shape === "horizontal" ? horizontalVolumeFraction(g) : g;
}

/** Gallons currently in a tank for a gauge percentage. */
export function gallonsInTank(
  nominalGallons: number,
  gaugePercent: number,
  shape: TankShape = "horizontal",
): number {
  return nominalGallons * volumeFraction(gaugePercent, shape);
}

/** Room left before the tank hits working capacity. */
export function roomToFill(
  nominalGallons: number,
  gaugePercent: number,
  shape: TankShape = "horizontal",
): number {
  const working = nominalGallons * WORKING_CAPACITY_FRACTION;
  return Math.max(0, Math.round(working - gallonsInTank(nominalGallons, gaugePercent, shape)));
}